import React from 'react';
import { INPUT_STYLE } from 'app/components/forms/Input';
import { RED_G } from 'app/constants/Colors';
import throttle from 'lodash/throttle';
import { animationFrameDebounce, idleCallbackDebouce } from 'app/utils/async';
import OnWindowEvent from 'app/components/layout/OnWindowEvent';
import { useClasses } from 'app/utils/css';
import { autoFocusAtEnd } from './inputUtils';
import { ExtractProps } from 'app/utils/types';
import { createSelector } from 'reselect';

const TYPING_TIMEOUT = 300;

export interface IProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  value?: string;
  onChange?: (value: string) => void;
  autoResize?: boolean;
  minHeight?: string;
  maxHeightPx?: number;
  hasLabel?: boolean;
  error?: boolean;
  immediate?: boolean;
  getRef?: React.Ref<HTMLTextAreaElement>;
  noBorder?: boolean;
  autoFocusAtEnd?: boolean;
  backgroundTransparent?: boolean;
}

interface IState {
  value: string;
}

export default class TextArea extends React.Component<IProps, IState> {
  /**
   * @param {Object}   props
   * @param {string}   props.value      - value for the field
   * @param {Function} props.onChange   - notified whenever value
   *                                      changes, debounced by
   *                                      default by 300ms
   * @param {bool}     props.immediate  - disable debouncing of `onChange`
   * @param {bool}     props.autoResize - automatically adjust height to the content
   * @param {bool}     props.hasLabel   - show label
   * @param {bool}     props.error      - if set, will change border color to indicate an error.
   * @param {bool}     props.noBorder   - if set, will hide the border,
   *                                      when used, the error flag has no effect
   * @param {bool}     props.backgroundTransparent   - if set, will set background color
   *                                      to be transparent
   */
  public static defaultProps = {
    autoResize: false,
    hasLabel: false,
    immediate: false,
    noBorder: false,
    error: false,
    autoFocusAtEnd: false,
    backgroundTransparent: false,
  };

  private textarea: HTMLTextAreaElement | null = null;
  private sizeHelper: HTMLDivElement | null = null;

  public constructor(props: IProps) {
    super(props);
    this.state = { value: props.value ? props.value : '' };
  }

  public componentDidMount() {
    this.resizeTextArea();

    if (this.props.autoFocusAtEnd && this.textarea) {
      autoFocusAtEnd(this.textarea);
    }
  }

  public shouldComponentUpdate(nextProps: IProps) {
    if (this.props.value !== nextProps.value) {
      // If the value in the props changes, don't update until we also set
      // the local state to avoid issues with the cursor jumping in some cases.
      this.setState({ value: nextProps.value ? nextProps.value : '' });
      return false;
    }
    return true;
  }

  public componentDidUpdate(oldProps: IProps, { value: oldValue }: IState) {
    const { value: newValue } = this.state;

    if (newValue !== oldValue) {
      this.resizeTextArea();
    }
  }

  public componentWillUnmount() {
    this.debouncedOnChange.cancel();
    this.onChangeCallbackThrottled.cancel();
    this.doResizeDebounced.cancel();
  }

  private onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setValue(event.target.value);
  };

  private onChangeCallback = () => {
    const value = this.state.value;
    this.props.onChange && this.props.onChange(value);
  };

  // In addition to throttling the onChange callback, also debounce it using requestIdleCallback,
  // to ensure that we don't call onChange until the browser isn't overloaded.
  private debouncedOnChange = idleCallbackDebouce(this.onChangeCallback);
  private onChangeCallbackThrottled = throttle(this.debouncedOnChange, TYPING_TIMEOUT);

  private setValue = (value: string) => {
    this.setState({ value }, () => {
      // notifyChange will reference this.state.value, so we must wait until the setState has
      // resolved before triggering it.
      this.notifyChange();
    });
  };

  private notifyChange = () => {
    if (this.props.immediate) {
      this.onChangeCallback();
    } else {
      this.onChangeCallbackThrottled();
    }
  };

  private resizeTextArea = () => {
    if (this.props.autoResize && this.textarea && this.sizeHelper) {
      this.doResizeDebounced();
    }
  };

  private doResize = () => {
    // Use hidden <div> to calculate height of <textarea>'s content and resize it
    if (!this.sizeHelper || !this.textarea) {
      return;
    }

    this.sizeHelper.innerText = `${this.textarea.value}\n`;

    const textHeight = this.sizeHelper ? this.sizeHelper.offsetHeight : 0;
    const newHeight = (
      this.props.maxHeightPx ?
        Math.min(textHeight, this.props.maxHeightPx) :
        textHeight
    );

    if (this.textarea) {
      this.textarea.style.height = `${newHeight}px`;
    }

    // Hide scrollbar until we hit max height, to avoid flash of scrollbar as we auto-resize.
    const allowScroll = this.props.maxHeightPx ? textHeight > this.props.maxHeightPx : false;
    if (this.textarea) {
      this.textarea.style.overflowY = allowScroll ? 'auto' : 'hidden';
    }
  };

  private doResizeDebounced = animationFrameDebounce(this.doResize);

  // `getNode` replicate React's behavior for `ref` prop
  // Ensure `getNode` is updated when `getRef` from prop changes
  // and is memoised to prevent unnecessary recreation
  private getNode = createSelector(
    ({ getRef }: IProps) => getRef,
    (getRef) => (node: HTMLTextAreaElement | null) => {
      this.textarea = node;

      if (getRef && typeof getRef === 'object') {
        // In normal situation, REACT should set the value
        // We are imitating REACT's behavior

        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        getRef.current = node;
      } else if (typeof getRef === 'function') {
        getRef(node);
      }
    },
  );

  private getSizeHelperNode = (node: HTMLDivElement | null) => { this.sizeHelper = node; };

  public render() {
    const {
      // remove value and onChange from props
      /* eslint-disable @typescript-eslint/no-unused-vars */
      value,
      onChange,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...restProps
    } = this.props;

    return (
      <OnWindowEvent event="resize" handler={this.resizeTextArea}>
        <TextAreaWrapper
          getSizeHelperNode={this.getSizeHelperNode}
          getNode={this.getNode(this.props)}
          onChange={this.onChange}
          {...restProps}
          {...this.state}
        />
      </OnWindowEvent>
    );
  }
}

interface ITextAreaWrapperProps extends Omit<ExtractProps<typeof TextArea>, 'onChange'> {
  getSizeHelperNode: (node: HTMLDivElement | null) => void,
  getNode: (node: HTMLTextAreaElement | null) => void,
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void,
}

export const textareaBasicStyle = {
  container: {
    zIndex: 0,
    position: 'relative',
    // Make the container display: flex so the <textarea> fills the container.
    display: 'flex',
    // Make the textarea container fill its container.
    flexGrow: 1,
    flexShrink: 1,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
  },
  textarea: {
    position: 'relative',
    zIndex: 1,
    lineHeight: 1.5,
    padding: '0 5px',
    // Don't let the user resize the textarea since it will auto-resize.
    resize: 'none',
    overflowX: 'auto',
    // overflow-y will be set dynamically
  },
} as const;

const TextAreaWrapper = (props: ITextAreaWrapperProps) => {
  const {
    autoResize,
    hasLabel,
    noBorder,
    error,
    // don't pass component props to <textarea> element
    /* eslint-disable */
    immediate,
    minHeight,
    maxHeightPx,
    getRef,
    autoFocusAtEnd,
    /* eslint-enable */
    getSizeHelperNode,
    getNode,
    backgroundTransparent,
    ...attrs
  } = props;

  const textAreaStyles =  [
    {
      ...INPUT_STYLE,
      ...textareaBasicStyle.textarea,
    },

    backgroundTransparent && {
      backgroundColor: 'transparent',
    },

    Boolean(minHeight) && {
      minHeight,
    },

    !autoResize && {
      minHeight: '3em',
    },

    hasLabel && {
      borderTopLeftRadius: '0px',
      borderBottomLeftRadius: '0px',
    },

    noBorder && {
      border: 'none',
    },

    error && {
      borderColor: RED_G,
    },
  ] as const;

  const classes =  useClasses({
    container: textareaBasicStyle.container,
    textArea: [...textAreaStyles],
    textAreaWithSizeHelper: [
      ...textAreaStyles,
      {
        // Make it absolutely positioned and hidden, so it does not affect the layout
        position: 'absolute',
        visibility: 'hidden',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 0,

        // Make its text wrapping behave more like a <textarea>
        whiteSpace: 'pre-wrap',
        overflowWrap: 'break-word',
      },
    ],
  });

  return (
    <div className={classes.container}>
      {autoResize && (
        <div
          ref={getSizeHelperNode}
          className={classes.textAreaWithSizeHelper}
        />
      )}

      <textarea
        ref={getNode}
        cols={1}
        rows={1}
        className={classes.textArea}
        {...attrs}
      />
    </div>
  );
};
